import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { Condition, ConditionAddress, ConditionGroup, MediaElement, MediaText } from 'editor/src/store/design/types';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk } from 'editor/src/store/hooks';

import toastController from 'editor/src/util/toastController';

function isMediaText(element: MediaElement): element is MediaText {
  return element.type === 'text';
}

function getLinkedTextElements(
  conditionGroup: ConditionGroup,
  condition: Condition,
  optionId: string,
  media: MediaElement[],
): MediaText[] {
  const children = conditionGroup.children[getConditionChildrenKey(condition.id, optionId)];
  if (!children) {
    return [];
  }
  return children
    .map((child) => media.find((el) => isMediaText(el) && el.uuid === child.id))
    .filter((el): el is MediaText => !!el);
}

const applyStyleToAllConditionTextsOperation =
  (spreadIndex: number, conditionAddress: ConditionAddress, condition: Condition): Thunk =>
  (dispatch, getState, { i18n }) => {
    const state = getState();
    const spread = state.design.designData?.spreads[spreadIndex];
    const media = spread?.pages[0]?.groups.media;
    const conditionGroup = spread?.conditionGroup;
    if (!media || !conditionGroup) {
      return;
    }

    const sourceTexts = getLinkedTextElements(conditionGroup, condition, condition.activeOptionId, media);
    if (sourceTexts.length === 0) {
      toastController.warning(
        i18n.t('editor-apply-style-no-source-title', 'No text element'),
        i18n.t('editor-apply-style-no-source', 'Link a text element to the active option first'),
      );
      return;
    }

    const source = sourceTexts[0];
    let updatedCount = 0;

    batch(() => {
      dispatch(saveUndoRedoStateOperation('Apply style to all condition options'));

      condition.options.forEach((option) => {
        if (option.id === condition.activeOptionId) {
          return;
        }

        const targetTexts = getLinkedTextElements(conditionGroup, condition, option.id, media);
        targetTexts.forEach((target) => {
          const pageIndex = 0;
          const elementIndex = media.indexOf(target);
          if (elementIndex === -1) {
            return;
          }

          dispatch(
            updateMediaElementAction({
              elementAddress: { spreadIndex, pageIndex, elementIndex },
              elementUpdate: {
                x: source.x,
                y: source.y,
                width: source.width,
                height: source.height,
                r: source.r,
                sx: source.sx,
                sy: source.sy,
                fill: source.fill,
                extra: {
                  ...target.extra,
                  fontSize: source.extra.fontSize,
                  fontFamily: source.extra.fontFamily,
                  textAlign: source.extra.textAlign,
                  lineHeight: source.extra.lineHeight,
                  underline: source.extra.underline,
                  linethrough: source.extra.linethrough,
                  letterSpacing: source.extra.letterSpacing,
                  stroke: source.extra.stroke,
                  curve: source.extra.curve,
                  shadow: source.extra.shadow,
                  direction: source.extra.direction,
                  flexibleWidth: source.extra.flexibleWidth,
                  wrapping: source.extra.wrapping,
                  customFontLayout: source.extra.customFontLayout,
                },
              },
            }),
          );
          updatedCount += 1;
        });
      });
    });

    if (updatedCount > 0) {
      toastController.success(
        i18n.t('editor-apply-style-success-title', 'Style applied'),
        i18n.t('editor-apply-style-success', 'Text style applied to {{count}} element(s)', { count: updatedCount }),
      );
    } else {
      toastController.warning(
        i18n.t('editor-apply-style-no-targets-title', 'No targets'),
        i18n.t('editor-apply-style-no-targets', 'No text elements linked to other options'),
      );
    }
  };

export default applyStyleToAllConditionTextsOperation;
